import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { Camera, ChevronRight } from 'lucide-react';
import VenueBottomNav from '../../components/VenueBottomNav';
import VenueHeader from '../../components/VenueHeader';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { hostCardStyle } from '../../components/HostSettingsCard';
import SafeAvatarImage from '../../components/SafeAvatarImage';
import ProfileThemeFlash from '../../components/ProfileThemeFlash';
import ProfilePalettePicker from '../../components/ProfilePalettePicker';
import LogoutConfirmModal from '../../components/LogoutConfirmModal';
import { useVenueRequiredSteps } from '../../hooks/useVenueRequiredSteps';

const DANGER = '#E53935';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";
const cardStyle = {
  ...hostCardStyle,
  backgroundColor: 'var(--app-bg)',
  borderRadius: 20,
  border: '1px solid var(--card-border)',
};

export default function VenueProfilePage({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [uploading, setUploading] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  // Red dot next to "Venue Settings" when any required onboarding step
  // is missing — mirrors the badges inside the settings page itself so
  // owners who skip the top-level banner can't miss the reminder.
  const { hasMissing: hasMissingVenueSteps, missingKeys: venueMissingKeys } = useVenueRequiredSteps();

  const requestLogout = () => setConfirmLogout(true);
  const handleLogout = () => {
    setConfirmLogout(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onLogout) onLogout();
    window.location.href = '/login';
  };

  const handlePhotoClick = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/auth/me/photo`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedUser = res.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const photoSrc = user.photo_url
    ? (user.photo_url.startsWith('/') ? `${BACKEND_URL}${user.photo_url}` : user.photo_url)
    : null;

  const menuItems = [
    { label: 'Events', action: () => navigate('/venue/events') },
    {
      label: 'Venue Settings',
      action: () => navigate('/venue/settings'),
      missing: hasMissingVenueSteps,
      missingCount: venueMissingKeys.length,
    },
    { label: 'Account Settings', action: () => navigate('/venue/account-settings') },
    { label: 'Demo QR scan', action: () => navigate('/demo-scan') },
    { label: 'Allure Support', action: () => navigate('/support') },
    { label: 'Switch to..', action: () => navigate('/switch-role', { state: { fromPath: location.pathname } }) },
  ];

  return (
    <div className="app-shell" data-testid="venue-profile-page" style={{
      minHeight: '100vh', backgroundColor: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 120,
    }}>
      <ProfileThemeFlash />
      {/* Header — venue selector + bell */}
      <VenueHeader />

      {/* Logo — bigger and lower */}
      <div style={{ marginTop: 18 }}>
        <RouteTransitionLogo width={132} height={118} dataTestId="venue-profile-shared-logo" />
      </div>

      {/* Profile card — with gradient tint */}
      <div className="profile-name-card" style={{
        ...cardStyle, width: 'calc(100% - 48px)', maxWidth: 340, marginTop: 12,
        background: 'var(--profile-card-gradient)',
        border: 'none',
        boxShadow: 'none',
        padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <div style={{ position: 'relative' }}>
          <div
            onClick={handlePhotoClick}
            data-testid="venue-profile-photo-target"
            style={{
              width: 72, height: 72, borderRadius: '50%',
              backgroundColor: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid var(--card-border)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              overflow: 'hidden', cursor: uploading ? 'wait' : 'pointer',
              opacity: uploading ? 0.7 : 1,
              transition: 'opacity 200ms ease',
            }}
          >
            <SafeAvatarImage
              src={photoSrc}
              alt="Profile"
              dataTestId="venue-profile-photo-image"
              fallback={(
              <span style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text-2)' }}>
                {(user.name || 'N').charAt(0)}
              </span>
              )}
            />
          </div>
          <div
            onClick={handlePhotoClick}
            role="button"
            aria-label="Change profile photo"
            data-testid="venue-profile-photo-edit-btn"
            style={{ position: 'absolute', bottom: -2, left: -2, width: 26, height: 26, borderRadius: '50%', backgroundColor: 'var(--surface)', border: '1px solid var(--accent-purple)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
          >
            <Camera size={13} color="var(--accent-purple)" />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
            data-testid="venue-profile-photo-input"
          />
        </div>
        <div>
          <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{user.name || 'Venue Owner'}</p>
          <p style={{ fontFamily: font, fontSize: 14, fontWeight: 400, color: 'var(--text-3)', margin: 0 }}>Venue owner</p>
        </div>
      </div>

      {/* Palette preview strip — tap a dot to retint the whole app live. */}
      <div style={{ width: 'calc(100% - 48px)', maxWidth: 340 }}>
        <ProfilePalettePicker testIdPrefix="venue-profile-palette" />
      </div>

      {/* Menu items */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%', maxWidth: 340 }}>
        {menuItems.map((item) => (
          <button key={item.label} onClick={item.action}
            data-testid={`venue-profile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            data-missing={item.missing ? 'true' : undefined}
            style={{ ...hostCardStyle, width: '100%', fontFamily: font, fontSize: 17, fontWeight: 400, color: 'var(--text-2)', background: 'var(--surface)', border: '1px solid var(--card-border)', cursor: 'pointer', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left' }}>
            <span>{item.label}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {item.missing ? (
                <span
                  data-testid={`venue-profile-${item.label.toLowerCase().replace(/\s+/g, '-')}-incomplete-badge`}
                  aria-label={item.missingCount ? `${item.missingCount} required steps missing` : 'Required steps missing'}
                  title="Required steps missing"
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: DANGER,
                    boxShadow: '0 0 0 3px rgba(229, 57, 53, 0.18)',
                    flexShrink: 0,
                  }}
                />
              ) : null}
              <ChevronRight size={18} color="var(--text-2)" />
            </span>
          </button>
        ))}
        <button onClick={requestLogout} data-testid="venue-profile-logout"
          style={{ ...hostCardStyle, width: '100%', fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--accent-purple)', background: 'var(--surface)', border: '1px solid var(--accent-purple)', cursor: 'pointer', padding: '14px 16px', marginTop: 2 }}>
          Log Out
        </button>
      </div>

      <VenueBottomNav />
      <LogoutConfirmModal
        open={confirmLogout}
        onCancel={() => setConfirmLogout(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}
